import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Text, VStack } from "@chakra-ui/react";
import type { Dayjs } from "dayjs";
import { SCHEDULED_BUDGET_MAX_SEGMENTS } from "@common/utils/campaignBudgetMeta";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { BudgetData, BudgetPhaseForm } from "./BudgetSelector";
import type { PhaseCalendarNeighborBounds } from "./ScheduledPhaseRangePicker";
import { guidedBudgetPrimaryColor } from "./guidedBudgetEditorTokens";
import { GuidedBudgetPhaseRow } from "./GuidedBudgetPhaseRow";
import type { ScheduledBudgetAdDeliveryWindowUtc } from "../../utils/eventWallTime";
import type { FieldErrors } from "react-hook-form";
import type { ScheduledBudgetEditPolicy } from "../../utils/scheduledBudgetEditPolicy";
import {
  scheduledBudgetHasPerSegmentFieldErrors,
  scheduledBudgetSegmentFieldErrors,
  scheduledBudgetSegmentInvalidAmount,
  scheduledBudgetSegmentInvalidTimeField,
  scheduledBudgetSegmentRowMessage,
} from "../../utils/scheduledBudgetRhfErrors";

type GuidedBudgetScheduledPhasesSectionProps = {
  scheduledSegments: BudgetPhaseForm[];
  eventTimezone: string;
  deliveryWindowInEventTimezone: { start: Dayjs; end: Dayjs } | null;
  scheduledBudgetDeliveryWindow: ScheduledBudgetAdDeliveryWindowUtc | null;
  scheduledBudgetEditPolicy: ScheduledBudgetEditPolicy;
  phaseNeighborPickerBounds: Array<PhaseCalendarNeighborBounds | null>;
  scheduledPhaseTimelineError: string | null;
  scheduledBudgetFormErrors?: FieldErrors<BudgetData>;
  budget?: BudgetData;
  onAddSegment?: () => void;
  onRemoveSegment?: (index: number) => void;
  onChangeSegment?: (index: number, patch: Partial<BudgetPhaseForm>) => void;
};

export function GuidedBudgetScheduledPhasesSection({
  scheduledSegments,
  eventTimezone,
  deliveryWindowInEventTimezone,
  scheduledBudgetDeliveryWindow,
  scheduledBudgetEditPolicy,
  phaseNeighborPickerBounds,
  scheduledPhaseTimelineError,
  scheduledBudgetFormErrors,
  budget,
  onAddSegment,
  onRemoveSegment,
  onChangeSegment,
}: GuidedBudgetScheduledPhasesSectionProps) {
  const atMaxPhases = scheduledSegments.length >= SCHEDULED_BUDGET_MAX_SEGMENTS;
  const hasPerRowErrors = scheduledBudgetHasPerSegmentFieldErrors(
    scheduledBudgetFormErrors
  );
  const showGlobalTimelineAlert =
    Boolean(scheduledPhaseTimelineError) && !hasPerRowErrors;
  return (
    <>
      <Box h="1px" bg="gray.200" w="full" flexShrink={0} />

      {scheduledSegments.length < 2 ? (
        <Text fontSize="sm" color="gray.600" lineHeight="short" px={0.5}>
          Scheduled budgets need at least two phases. Set{" "}
          <Text as="span" fontWeight="semibold">
            Campaign run dates
          </Text>{" "}
          above so phase times can populate, or use{" "}
          <Text as="span" fontWeight="semibold">
            Add Budget Phase
          </Text>
          .
        </Text>
      ) : null}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <VStack align="stretch" spacing={3} w="full" pb={3}>
          {scheduledSegments.map((s, index) => {
            const phaseRow = scheduledBudgetEditPolicy.phaseRow(
              s,
              index,
              eventTimezone
            );
            const rowErrors = scheduledBudgetSegmentFieldErrors(
              scheduledBudgetFormErrors,
              index
            );
            return (
              <GuidedBudgetPhaseRow
              key={index}
              index={index}
              segment={s}
              segmentCount={scheduledSegments.length}
              eventTimezone={eventTimezone}
              deliveryWindowInEventTimezone={deliveryWindowInEventTimezone}
              neighborPickerBounds={phaseNeighborPickerBounds[index] ?? null}
              scheduledBudgetDeliveryWindow={scheduledBudgetDeliveryWindow}
              budget={budget}
              rowValidationMessage={scheduledBudgetSegmentRowMessage(rowErrors)}
              invalidTimeField={scheduledBudgetSegmentInvalidTimeField(rowErrors)}
              invalidAmount={scheduledBudgetSegmentInvalidAmount(rowErrors)}
              isCompletedPhase={phaseRow.isCompletedPhase}
              isLockedPhase={phaseRow.isLockedPhase}
              lockReason={phaseRow.lockReason}
              capPhaseStartAtNow={phaseRow.capPhaseStartAtNow}
              onChangeSegment={onChangeSegment}
              onRemoveSegment={onRemoveSegment}
              />
            );
          })}
        </VStack>
        {showGlobalTimelineAlert ? (
          <Text
            fontSize="sm"
            color="red.600"
            lineHeight="short"
            px={0.5}
            role="alert"
          >
            {scheduledPhaseTimelineError}
          </Text>
        ) : null}
        <Text fontSize="xs" color="gray.500" lineHeight="short" px={0.5}>
          {`Phases snap to 15-minute intervals and must be at least 3 hours long, within your campaign's run dates. You can add up to ${SCHEDULED_BUDGET_MAX_SEGMENTS} phases.`}
        </Text>
      </LocalizationProvider>

      <Button
        alignSelf="center"
        bg={guidedBudgetPrimaryColor}
        color="white"
        borderRadius="10px"
        px={3}
        py={2.5}
        h="auto"
        fontWeight="medium"
        fontSize="sm"
        lineHeight="19.2px"
        borderWidth="1px"
        borderColor="#E2E2E2"
        boxShadow="0px 2px 6px rgba(0, 0, 0, 0.05)"
        _hover={{ bg: "gray.800" }}
        _active={{ bg: "gray.900" }}
        rightIcon={<AddIcon boxSize={4} />}
        isDisabled={atMaxPhases}
        onClick={() => onAddSegment?.()}
      >
        Add Budget Phase
      </Button>
    </>
  );
}
